import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { categoryApi } from '@/services/api';
import { ContentCategory, ContentCategoryType } from '@/types';

interface CategorySelectFieldProps {
  type: ContentCategoryType;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  canManageCategories?: boolean;
}

const TYPE_LABELS: Record<ContentCategoryType, string> = {
  course: 'course',
  sports: 'sports listing',
  event: 'event or workshop',
};

export const CategorySelectField = ({
  type,
  value,
  onChange,
  error,
  canManageCategories = true,
}: CategorySelectFieldProps) => {
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError('');

    categoryApi
      .getCategories({ type, signal: controller.signal })
      .then(setCategories)
      .catch((requestError: Error) => {
        if (requestError.name !== 'AbortError') {
          setLoadError(requestError.message || 'Categories could not be loaded.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [type]);

  const hasCategories = categories.length > 0;

  return (
    <div>
      <label className="form-label">Category</label>
      <select
        className="form-control min-h-11"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={isLoading || Boolean(loadError)}
        aria-invalid={Boolean(error)}
      >
        <option value="">
          {isLoading ? 'Loading categories...' : hasCategories ? 'Select a category' : 'No categories available'}
        </option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>
      {error ? <p className="mt-1.5 text-sm font-medium text-red-600">{error}</p> : null}
      {loadError ? <p className="mt-1.5 text-sm font-medium text-red-600">{loadError}</p> : null}
      {!isLoading && !loadError && !hasCategories ? (
        <p className="mt-1.5 text-sm text-amber-700">
          No categories exist for this {TYPE_LABELS[type]} yet.
          {canManageCategories ? (
            <>
              {' '}
              <Link className="font-semibold underline" to="/admin/categories">
                Create one in Categories
              </Link>
              .
            </>
          ) : (
            ' Ask an administrator to create one before saving.'
          )}
        </p>
      ) : null}
    </div>
  );
};
